import React from "react";
import { Shield, Mail, Instagram } from "lucide-react";
import { useContent } from "../context/ContentContext";

const XIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const platformIcon = (platform) => {
  switch (platform) {
    case "instagram":
      return <Instagram className="w-4 h-4" />;
    case "x":
    case "twitter":
      return <XIcon className="w-4 h-4" />;
    default:
      return null;
  }
};

const Footer = () => {
  const { player, socialLinks } = useContent();
  return (
    <footer className="relative bg-black border-t border-neutral-900">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 rounded-sm bg-red-600 text-white">
              <Shield className="w-5 h-5" />
            </span>
            <div>
              <div className="font-display text-2xl text-white tracking-wider">
                {player.firstName.toUpperCase()}{" "}
                <span className="text-red-500">{player.lastName.toUpperCase()}</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-heading">
                #{player.jerseyNumber} · DE / DT · Class of {player.classOf}
              </div>
            </div>
          </div>
          <p className="mt-5 text-neutral-400 max-w-md text-sm">
            Two-way defensive lineman out of Fishers, IN. Built different. Built to dominate.
          </p>

          {socialLinks && socialLinks.length > 0 && (
            <div className="mt-6 flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="group flex items-center gap-2 px-3 h-10 rounded-sm border border-neutral-800 hover:border-red-600 bg-neutral-900/50 hover:bg-red-600/10 text-neutral-300 hover:text-red-500 transition-colors"
                >
                  {platformIcon(s.platform)}
                  <span className="text-xs font-heading uppercase tracking-[0.2em]">
                    {s.handle}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-heading mb-4">
            Navigate
          </div>
          <ul className="space-y-2 text-neutral-300 text-sm">
            <li><a href="#about" className="hover:text-red-500">About</a></li>
            <li><a href="#stats" className="hover:text-red-500">Stats</a></li>
            <li><a href="#film" className="hover:text-red-500">Film</a></li>
            <li><a href="#accolades" className="hover:text-red-500">Accolades</a></li>
            <li><a href="#contact" className="hover:text-red-500">Contact</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-heading mb-4">
            Reach Out
          </div>
          <a href={`mailto:${player.recruitingEmail}`} className="flex items-center gap-2 text-neutral-200 hover:text-red-500">
            <Mail className="w-4 h-4" /> {player.recruitingEmail}
          </a>
          <a href={`mailto:${player.contactEmail}`} className="flex items-center gap-2 text-neutral-200 hover:text-red-500 mt-2">
            <Mail className="w-4 h-4" /> {player.contactEmail}
          </a>
          {player.coachEmail && (
            <a href={`mailto:${player.coachEmail}`} className="flex items-center gap-2 text-neutral-200 hover:text-red-500 mt-2">
              <Mail className="w-4 h-4" /> {player.coachEmail}
              <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 ml-1">(Coach)</span>
            </a>
          )}
        </div>
      </div>
      <div className="border-t border-neutral-900">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-neutral-500">
            © {new Date().getFullYear()} DaMarkas McNeese. All rights reserved.
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 font-heading">
            Built For The Trenches
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
